import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    cards = "cards",
}
export interface cardModel {
    fields: {
        title: string;
        description: string;
        buttonText: string;
        buttonLink: LinkFieldValue;
        scrollIntoViewButton: boolean;
        scrollToElementId: string;
        openInNewWindow: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.cards]: cardModel[];
    };
}

export interface ServiceCardsStyles {
    container?: InjectableCss;
    card?: InjectableCss;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    buttonWrapper?: InjectableCss;
    button?: ButtonPresentationProps;
}

export const serviceCardsStyles: ServiceCardsStyles = {
    container: {
        css: css`
            display: grid;
            gap: 22px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            grid-template-columns: repeat(1, 1fr);
                        `,
                        null,
                        css`
                            grid-template-columns: repeat(2, 1fr);
                        `,
                        css`
                            grid-template-columns: repeat(3, 1fr);
                        `,
                    ],
                    "min",
                )}
        `,
    },
    card: {
        css: css`
            background: #ffffff;
            border-radius: 8px;
            padding: 20px;
            display: flex;
            flex-direction: column;
        `,
    },
    title: {
        variant: "h4",
    },
    description: {
        variant: "p",
    },
    buttonWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            flex-grow: 1;
        `,
    },
    button: {
        variant: "secondary",
    },
};

const styles = serviceCardsStyles;

type GetLinkProps = {
    card: {
        fields: {
            buttonText: string;
            buttonLink: LinkFieldValue;
            openInNewWindow: boolean;
        };
    };
};

const GetLink: FC<GetLinkProps> = ({ card }) => {
    const buttonLink = useGetLink(card.fields.buttonLink);
    return (
        <StyledWrapper {...styles.buttonWrapper}>
            <Link href={buttonLink.url || ""} target={card.fields.openInNewWindow ? "_blank" : ""}>
                <Button {...styles.button}>{card.fields.buttonText}</Button>
            </Link>
        </StyledWrapper>
    );
};

export const ServiceCards: FC<OwnProps> = ({ fields }) => {
    const handleScrollTo = (id: string) => {
        if (id) {
            const elem = document.getElementById(id);
            if (elem) {
                const top = elem.offsetTop;
                window.scrollTo({
                    top: top - 50,
                    behavior: "smooth",
                });
            }
        }
    };

    return (
        <StyledWrapper {...styles.container}>
            {fields.cards.map((card, i) => {
                return (
                    <StyledWrapper {...styles.card} key={card.fields.title}>
                        <Typography {...styles.title}>{card.fields.title}</Typography>
                        <Typography {...styles.description}>{card.fields.description}</Typography>
                        {card.fields.buttonText &&
                            (card.fields.scrollIntoViewButton ? (
                                <StyledWrapper {...styles.buttonWrapper}>
                                    <Button
                                        {...styles.button}
                                        onClick={() => handleScrollTo(card.fields.scrollToElementId)}
                                    >
                                        {card.fields.buttonText}
                                    </Button>
                                </StyledWrapper>
                            ) : (
                                <GetLink card={card} />
                            ))}
                    </StyledWrapper>
                );
            })}
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: ServiceCards,
    definition: {
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.cards,
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "description",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "buttonText",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "scrollIntoViewButton",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "scrollToElementId",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.scrollIntoViewButton === true,
                    },
                    {
                        name: "buttonLink",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Url",
                        },
                        isVisible: widget => widget.fields.scrollIntoViewButton !== true,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: widget => widget.fields.scrollIntoViewButton !== true,
                    },
                ],
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
